import {
  decodeLegacyEmotionGraph,
  LEGACY_EMOTION_CELLS,
  type EncodedLegacyGraphState,
} from "./emotion-map-topology";

export interface EmotionDirectionSummary {
  x: number;
  y: number;
  activity: number;
  coherence: number;
  angle_rad: number | null;
}

export interface EmotionSynchronizationMeasurement {
  raw_rate_percent: number;
  alignment: number;
  confidence: number;
  driver: EmotionDirectionSummary;
  vehicle: EmotionDirectionSummary;
  interpretation: "same_direction" | "partly_aligned" | "neutral" | "opposed";
}

const clamp = (value: number, minimum: number, maximum: number): number =>
  Math.max(minimum, Math.min(maximum, value));

const smoothStep = (minimum: number, maximum: number, value: number): number => {
  const ratio = clamp((value - minimum) / (maximum - minimum), 0, 1);
  return ratio * ratio * (3 - 2 * ratio);
};

function directionSummary(encoded: EncodedLegacyGraphState): EmotionDirectionSummary {
  const graph = decodeLegacyEmotionGraph(encoded);
  if (!graph) return { x: 0, y: 0, activity: 0, coherence: 0, angle_rad: null };
  let x = 0;
  let y = 0;
  let mass = 0;
  let availableMass = 0;
  for (const cell of LEGACY_EMOTION_CELLS) {
    const width = Math.max(0, cell.end - cell.start);
    const potential = clamp(graph.membranePotentials[cell.id] ?? 0, 0, 1);
    const weight = potential * width;
    const angle = cell.start + width / 2;
    x += weight * Math.cos(angle);
    y += weight * Math.sin(angle);
    mass += weight;
    availableMass += width;
  }
  const magnitude = Math.hypot(x, y);
  return {
    x,
    y,
    activity: availableMass > 0 ? mass / availableMass : 0,
    coherence: mass > 1e-12 ? clamp(magnitude / mass, 0, 1) : 0,
    angle_rad: magnitude > 1e-12 ? Math.atan2(y, x) : null,
  };
}

export function measureEmotionSynchronization(
  driverGraph: EncodedLegacyGraphState,
  vehicleGraph: EncodedLegacyGraphState,
): EmotionSynchronizationMeasurement {
  const driver = directionSummary(driverGraph);
  const vehicle = directionSummary(vehicleGraph);
  const driverMagnitude = Math.hypot(driver.x, driver.y);
  const vehicleMagnitude = Math.hypot(vehicle.x, vehicle.y);
  const alignment = driverMagnitude > 1e-12 && vehicleMagnitude > 1e-12
    ? clamp((driver.x * vehicle.x + driver.y * vehicle.y) / (driverMagnitude * vehicleMagnitude), -1, 1)
    : 0;

  // Directional confidence is lost only when a map is nearly empty or almost
  // isotropic. A broad distribution in the same half-plane remains strong
  // evidence of the same emotional direction.
  const activityConfidence = smoothStep(.005, .03, Math.min(driver.activity, vehicle.activity));
  const directionConfidence = smoothStep(.08, .25, Math.min(driver.coherence, vehicle.coherence));
  const confidence = activityConfidence * directionConfidence;
  const directionalRate = 50 + 50 * confidence * alignment;
  // Different angular breadth is a mild mismatch, not a reason to erase an
  // otherwise shared direction. A broad and a narrow map with the same centre
  // therefore stay near 100%, as required by the demo interpretation.
  const breadthPenalty = 20 * confidence * Math.max(0, alignment)
    * Math.abs(driver.coherence - vehicle.coherence);
  const rawRate = clamp(directionalRate - breadthPenalty, 0, 100);
  const interpretation = rawRate >= 80 ? "same_direction"
    : rawRate > 55 ? "partly_aligned"
      : rawRate >= 45 ? "neutral" : "opposed";
  return {
    raw_rate_percent: rawRate,
    alignment,
    confidence,
    driver,
    vehicle,
    interpretation,
  };
}

export class SynchronizationRateSmoother {
  private owner = "";
  private sampleId = "";
  private values: number[] = [];
  private readonly length: number;

  constructor(length = 5) {
    if (!Number.isInteger(length) || length < 1) throw new Error("synchronization history length differs");
    this.length = length;
  }

  update(owner: string, sampleId: string, value: number): number {
    if (owner !== this.owner) {
      this.owner = owner;
      this.sampleId = "";
      this.values = [];
    }
    if (sampleId !== this.sampleId) {
      this.sampleId = sampleId;
      this.values.push(clamp(value, 0, 100));
      if (this.values.length > this.length) this.values.shift();
    }
    return this.values.length
      ? this.values.reduce((sum, item) => sum + item, 0) / this.values.length
      : 50;
  }
}
